import { useParams } from 'react-router';

import { ApiError } from './client.ts';
import { useListAllProjects, useListClients } from './generated/clients.ts';
import type { Client, Project } from './generated/schemas.ts';

// A client's pages are `/clients/<slug>`, a project's `/projects/<slug>` whatever its client (plan/DECISIONS.md D185).
// The API refers to both by id; the lists of clients and of projects hold every one of the workspace, so the slugs are
// found in them.
export const clientPath = (client: Pick<Client, 'slug'>) => `/clients/${client.slug}`;

export const clientSettingsPath = (client: Pick<Client, 'slug'>) => `/clients/${client.slug}/settings`;

// Who works on it (plan/DECISIONS.md D180).
export const clientTeamPath = (client: Pick<Client, 'slug'>) => `/clients/${client.slug}/team`;

// Its people, their invitations and its invite link (D182).
export const clientPeoplePath = (client: Pick<Client, 'slug'>) => `/clients/${client.slug}/people`;

export const projectPath = (project: Pick<Project, 'slug'>) => `/projects/${project.slug}`;

export const projectSettingsPath = (project: Pick<Project, 'slug'>) => `${projectPath(project)}/settings`;

// Who works on it (D180).
export const projectTeamPath = (project: Pick<Project, 'slug'>) => `${projectPath(project)}/team`;

// Its client, and who of the client's people read it (D182, D184).
export const projectClientPath = (project: Pick<Project, 'slug'>) => `${projectPath(project)}/client`;

// Opens the project's published recaps without an account (plan/DECISIONS.md D103).
export const toPublicProjectLink = (token: string) => `${window.location.origin}/p/${token}`;

const notFound = () => new ApiError(404, 'not_found', 'Not found');

// The client of the URL; undefined while the list loads. Throws a 404 for a slug that isn't there, so the route's error
// element shows "not found".
export const useClientOfPath = () => {
    const { clientSlug } = useParams();
    const clients = useListClients();

    if (clients.error) throw clients.error;
    if (!clients.data) return undefined;
    const client = clients.data.find((item) => item.slug === clientSlug);
    if (!client) throw notFound();
    return client;
};

// The project of the URL and its client, null for the team's own (D184); undefined while the lists load. A 404 as for
// a client.
export const useProjectOfPath = () => {
    const { projectSlug } = useParams();
    const projects = useListAllProjects();
    const clients = useListClients();

    if (projects.error) throw projects.error;
    if (clients.error) throw clients.error;
    if (!projects.data || !clients.data) return undefined;
    const project = projects.data.find((item) => item.slug === projectSlug);
    if (!project) throw notFound();
    return { project, client: clients.data.find((client) => client.id === project.client?.id) ?? null };
};
